/**
 * VehicleInspectionsTab - Aba "Vistoria" do modal de veículo
 *
 * Lista as vistorias do veículo, cria nova vistoria escolhendo um Template de
 * Checklist ativo e gerencia as vistorias periódicas (diária/semanal/mensal).
 *
 * @version 1.5.5
 * RELEASE: 2026-10-05 v1.5.5 - Botões "Nova vistoria" e "Periodicidade" só aparecem
 *          para quem tem "Criar" em checklist_inspections / checklist_schedules.
 * RELEASE: 2026-09-29 v1.5.4 - Responsável obrigatório conforme o tipo: tipo
 *          "Usuário" exige usuário selecionado e "Fila global" exige fila para
 *          salvar (Nova vistoria e Periodicidade), com aviso inline; evita
 *          vistorias sem responsável e link externo bloqueado.
 * RELEASE: 2026-09-26 v1.5.3 - Linha de filtros das duas seções passa a ficar
 *          ancorada à direita (padrão da aba Financeiro), mantendo o limite
 *          da coluna "Tipo checklist" e o menu de reticências sempre visível.
 * RELEASE: 2026-09-26 v1.5.2 - Linha de filtros da aba Vistorias passa a iniciar
 *          alinhada sob a coluna "Tipo checklist" (posição medida no DOM) e sem
 *          rolagem horizontal: status em aberto ficam visíveis; estados finais
 *          (Aprovada/Reprovada/Cancelada) e filtros extras abrem no botão "..."
 *          sempre visível.
 * RELEASE: 2026-09-26 v1.5.1 - Restaura a hierarquia em duas linhas: abas e
 *          ações permanecem no cabeçalho; filtros pertencem somente à seção
 *          ativa, com opções complementares no menu de reticências.
 * RELEASE: 2026-09-26 v1.5.0 - Listagem exibe tipo, periodicidade, origem,
 *          responsáveis com avatar, datas completas e progresso visual; filtros
 *          foram consolidados em uma única faixa com menu avançado.
 * RELEASE: 2026-09-26 v1.4.1 - Detalhes da periodicidade usa altura definida,
 *          corpo com rolagem nativa e cabeçalho/rodapé fixos mesmo em modal aninhado.
 * RELEASE: 2026-09-26 v1.4.0 - Modais premium compartilhados, detalhes completos
 *          e edição de periodicidade pelo mesmo formulário da criação.
 * RELEASE: 2026-09-26 v1.3.0 - Nova vistoria/Periodicidade: escolha do Tipo de Checklist
 *          antes do template e do Responsável (Usuário, Fila global ou Proprietário).
 * RELEASE: 2026-09-26 v1.2.1 - Status da tabela passa ao padrão compartilhado
 *          de bolinha semântica com texto na cor de primeiro plano.
 * RELEASE: 2026-09-26 v1.2.0 - Faixa de filtros no padrão da aba Financeiro: separa
 *          Vistorias e Periodicidades, filtra por status, busca, alerta e atraso.
 * RELEASE: 2026-09-26 v1.1.0 - Periodicidade explicita horário de criação e
 *          ações seguem o tamanho compacto da aba Tarefas.
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial (Checklist Fase 2/2b)
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Plus, AlertTriangle, Repeat, Pause, Play, Square, ClipboardCheck, Eye, CalendarRange, UserRound, Settings2, MoreHorizontal, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import {
  DEADLINE_LABEL, INSPECTION_STATUS, useInspectionActions, useInspectionTemplatesForSelection,
  useVehicleInspectionsList, useVehicleSchedules, useVehicleInspectionsRealtime,
} from '@/hooks/useVehicleInspections';
import { InspectionDialog } from '@/components/checklist/inspection/InspectionDialog';
import { ChecklistTypeSelect } from '@/components/checklist/ChecklistTypesDialog';
import { SystemUserSelector } from '@/components/crm/SystemUserSelector';
import { useGlobalDistributionQueues } from '@/hooks/useChecklistTypes';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type AssigneeMode = 'user' | 'queue' | 'owner';
type ScheduleRow = Record<string, any> & {
  id: string; template_id: string; template_name: string; type_id?: string | null; type_name?: string | null;
  frequency: string; day_of_week: number; day_of_month: number; time_of_day: string; starts_on: string;
  ends_mode: string; ends_on?: string | null; max_occurrences?: number | null; occurrences_count?: number;
  skip_if_open: boolean; status: string; assignee_mode: AssigneeMode; assignee_user_id?: string | null;
  assignee_queue_id?: string | null; assignee_name?: string | null; queue_name?: string | null;
  next_dates?: string[]; last_run_at?: string | null; created_at?: string; updated_at?: string;
};

/** Campos de Responsável compartilhados por Nova vistoria e Periodicidade. */
function AssigneeFields({ open, mode, userId, queueId, onChange }: {
  open: boolean; mode: AssigneeMode; userId: string | null; queueId: string;
  onChange: (p: { mode?: AssigneeMode; userId?: string | null; queueId?: string }) => void;
}) {
  const { data: queues = [] } = useGlobalDistributionQueues(open && mode === 'queue');
  return (
    <div className="space-y-2">
      <Label>Responsável</Label>
      <Select value={mode} onValueChange={(v) => onChange({ mode: v as AssigneeMode })}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="user">Usuário</SelectItem>
          <SelectItem value="queue">Fila global</SelectItem>
          <SelectItem value="owner">Cliente/proprietário do veículo</SelectItem>
        </SelectContent>
      </Select>
      {mode === 'user' && (
        <>
          <SystemUserSelector value={userId} onSelect={(id) => onChange({ userId: id })} placeholder="Selecione o usuário responsável" allowClear />
          {!userId && <p className="text-xs text-warning">Selecione um usuário responsável para salvar.</p>}
        </>
      )}
      {mode === 'queue' && (
        <>
          <Select value={queueId} onValueChange={(v) => onChange({ queueId: v })}>
            <SelectTrigger><SelectValue placeholder="Selecione a fila" /></SelectTrigger>
            <SelectContent>{queues.map((q) => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}</SelectContent>
          </Select>
          {!queues.length && <p className="text-xs text-muted-foreground">Nenhuma fila global ativa. Crie uma em Filas de distribuição.</p>}
          {queues.length > 0 && !queueId && <p className="text-xs text-warning">Selecione a fila para salvar.</p>}
          <p className="text-xs text-muted-foreground">O usuário é escolhido pela regra configurada na fila.</p>
        </>
      )}
      {mode === 'owner' && (
        <p className="text-xs text-muted-foreground">A vistoria fica registrada no nome do proprietário atual do veículo; o preenchimento é feito por um usuário interno.</p>
      )}
    </div>
  );
}

/** Cor da bolinha por tom semântico (mesma do Kanban/StatusDotLabel). */
const TONE_DOT: Record<string, string> = {
  neutral: 'bg-muted-foreground', info: 'bg-primary', warning: 'bg-warning', danger: 'bg-destructive', success: 'bg-success',
};
/**
 * v1.5.2: status "em aberto" ficam sempre visíveis na linha de filtros; os
 * estados finais (Aprovada/Reprovada/Cancelada) ficam apenas no menu "...".
 */
const VISIBLE_STATUS_KEYS = ['pending', 'in_progress', 'awaiting_review', 'correction_requested'];
const HIDDEN_STATUS_KEYS = Object.keys(INSPECTION_STATUS).filter((k) => !VISIBLE_STATUS_KEYS.includes(k));
const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const FREQ: Record<string, string> = { daily: 'Diária', weekly: 'Semanal', monthly: 'Mensal' };
const ASSIGNEE_MODE: Record<AssigneeMode, string> = { user: 'Usuário', queue: 'Fila global', owner: 'Cliente/proprietário' };
const d = (v?: string | null, f = 'dd/MM/yyyy') => (v ? format(new Date(v), f, { locale: ptBR }) : '—');
/** Datas "YYYY-MM-DD" interpretadas como locais (sem deslocar o dia) */
const dLocal = (v: string) => { const [y, m, dd] = v.split('-').map(Number); return format(new Date(y, m - 1, dd), 'dd/MM (EEE)', { locale: ptBR }); };

export function VehicleInspectionsTab({ vehicleId }: { vehicleId: string }) {
  const { data: rows, isLoading } = useVehicleInspectionsList(vehicleId);
  const { data: schedules } = useVehicleSchedules(vehicleId);
  // Tempo real: novas vistorias/periodicidades aparecem sem F5.
  useVehicleInspectionsRealtime(vehicleId);
  const [openId, setOpenId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleRow | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<ScheduleRow | null>(null);
  const actions = useInspectionActions();
  // Permissões de criação (mesmas chaves validadas nas RPCs)
  const { canCreate } = usePermissions();
  const canCreateInspection = canCreate('checklist_inspections');
  const canCreateSchedule = canCreate('checklist_schedules');

  // v1.2.0: filtros locais (sobre os dados já carregados do veículo)
  const [section, setSection] = useState<'inspections' | 'schedules'>('inspections');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [schedFilter, setSchedFilter] = useState<string>('all');
  const [onlyFraud, setOnlyFraud] = useState(false);
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  // v1.5.2: alinhamento da linha de filtros sob a coluna "Tipo checklist" da tabela.
  // A tabela tem colunas automáticas, então a posição é medida no DOM e
  // reaplicada quando a largura disponível mudar (ResizeObserver).
  const typeThRef = useRef<HTMLTableCellElement | null>(null);
  const [filterIndent, setFilterIndent] = useState(0);
  useLayoutEffect(() => {
    if (section !== 'inspections') return;
    const th = typeThRef.current;
    const wrap = th?.closest('table')?.parentElement as HTMLElement | null;
    if (!th || !wrap) { setFilterIndent(0); return; }
    const measure = () => {
      const thRect = th.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      // + scrollLeft: alinha com o espaço da tabela em rolagem zero (o offset
      // real fica fixo mesmo se o usuário rolar a tabela depois).
      setFilterIndent(Math.max(0, thRect.left - wrapRect.left + wrap.scrollLeft));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    ro.observe(th);
    return () => ro.disconnect();
  }, [section, isLoading]);


  const statusCount = (k: string) => (rows ?? []).filter((r) => k === 'all' || r.status === k).length;
  const filteredRows = useMemo(() => {
    return (rows ?? []).filter((r) =>
      (statusFilter === 'all' || r.status === statusFilter)
      && (!onlyFraud || r.fraud_alerts > 0)
      && (!onlyOverdue || r.deadline_status.includes('overdue')));
  }, [rows, statusFilter, onlyFraud, onlyOverdue]);
  const filteredSchedules = ((schedules ?? []) as ScheduleRow[]).filter((s) => schedFilter === 'all' || s.status === schedFilter);

  /** Botão pílula no padrão da aba Financeiro (bolinha + texto). */
  const Pill = ({ active, onClick, dot, label, count }: { active: boolean; onClick: () => void; dot?: string; label: string; count?: number }) => (
    <Button variant={active ? 'secondary' : 'ghost'} size="sm" className="h-6 text-xs px-2" onClick={onClick}>
      {dot && <span className={cn('w-1.5 h-1.5 rounded-full mr-1', dot)} />}
      {label}{count !== undefined && <span className="ml-1 text-[10px] text-muted-foreground">{count}</span>}
    </Button>
  );

  return (
    <div className="space-y-3">
      {/* Linha principal: navegação entre seções e ações sempre visíveis. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Pill active={section === 'inspections'} onClick={() => setSection('inspections')} label="Vistorias" count={rows?.length ?? 0} />
          <Pill active={section === 'schedules'} onClick={() => setSection('schedules')} label="Periodicidades" count={schedules?.length ?? 0} />
        </div>
        <div className="flex shrink-0 gap-2">
          {canCreateSchedule && <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={() => setSchedOpen(true)}><Repeat className="h-3.5 w-3.5" />Periodicidade</Button>}
          {canCreateInspection && <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => setNewOpen(true)}><Plus className="h-3.5 w-3.5" />Nova vistoria</Button>}
        </div>
      </div>

      {/* Linha secundária: filtros exclusivos da seção selecionada.
          v1.5.3: alinhamento à direita no padrão da aba Financeiro (pílulas
          começam da borda direita para a esquerda), sem rolagem horizontal —
          a linha respeita o limite da coluna "Tipo checklist", os status em
          aberto ficam visíveis e os demais filtros abrem no menu de
          reticências (sempre visível). */}
      {section === 'inspections' ? (
        <div className="flex flex-wrap items-center justify-end gap-1" style={{ paddingLeft: filterIndent }}>

          <Pill active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label="Todos" count={statusCount('all')} />
          {VISIBLE_STATUS_KEYS.map((k) => (
            <Pill key={k} active={statusFilter === k} onClick={() => setStatusFilter(k)} dot={TONE_DOT[INSPECTION_STATUS[k].tone]} label={INSPECTION_STATUS[k].label} count={statusCount(k)} />
          ))}
          <TooltipProvider>
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="relative h-7 w-7 shrink-0" aria-label="Mais filtros">
                      <MoreHorizontal className="h-4 w-4" />
                      {(onlyFraud || onlyOverdue || HIDDEN_STATUS_KEYS.includes(statusFilter)) && (
                        <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>Mais filtros</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="start" className="w-60">
                {/* Estados finais: seleção exclusiva, no padrão das pílulas */}
                {HIDDEN_STATUS_KEYS.map((k) => (
                  <DropdownMenuItem key={k} className="gap-2" onSelect={() => setStatusFilter(k)}>
                    <span className={cn('h-2 w-2 shrink-0 rounded-full', TONE_DOT[INSPECTION_STATUS[k].tone])} />
                    <span className="flex-1">{INSPECTION_STATUS[k].label}</span>
                    <span className="text-[10px] tabular-nums text-muted-foreground">{statusCount(k)}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem checked={onlyFraud} onCheckedChange={setOnlyFraud}>Com alerta de fraude</DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem checked={onlyOverdue} onCheckedChange={setOnlyOverdue}>Atrasadas</DropdownMenuCheckboxItem>
                {(onlyFraud || onlyOverdue || HIDDEN_STATUS_KEYS.includes(statusFilter)) && <DropdownMenuSeparator />}
                {(onlyFraud || onlyOverdue || HIDDEN_STATUS_KEYS.includes(statusFilter)) && (
                  <DropdownMenuItem onSelect={() => { setOnlyFraud(false); setOnlyOverdue(false); setStatusFilter('all'); }}>
                    <RotateCcw className="mr-2 h-3.5 w-3.5" />Limpar filtros
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </TooltipProvider>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-1">
          {[{ k: 'all', l: 'Todas', d: '' }, { k: 'active', l: 'Ativas', d: 'bg-success' }, { k: 'paused', l: 'Pausadas', d: 'bg-warning' }, { k: 'ended', l: 'Encerradas', d: 'bg-muted-foreground' }].map((o) => (
            <Pill key={o.k} active={schedFilter === o.k} onClick={() => setSchedFilter(o.k)} dot={o.d || undefined} label={o.l}
              count={(schedules ?? []).filter((s) => o.k === 'all' || s.status === o.k).length} />
          ))}
        </div>
      )}

      {/* Regras periódicas do veículo */}
      {section === 'schedules' && (filteredSchedules.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma periodicidade neste filtro.</p>
      ) : (
        <div className="space-y-2">
          {filteredSchedules.map((s) => (
            <div key={s.id} role="button" tabIndex={0} className="rounded-md border p-2 text-xs flex flex-wrap items-center gap-2 cursor-pointer hover:bg-muted/30"
              onClick={() => setSelectedSchedule(s)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') setSelectedSchedule(s); }}>
              <Repeat className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium">{s.template_name}</span>
              <span>{FREQ[s.frequency]}{s.frequency === 'weekly' ? ` · ${WEEKDAYS[s.day_of_week]}` : ''}{s.frequency === 'monthly' ? ` · dia ${s.day_of_month}` : ''} · {String(s.time_of_day).slice(0, 5)}</span>
              <Badge variant="outline" className="font-normal">{s.status === 'active' ? 'Ativa' : s.status === 'paused' ? 'Pausada' : 'Encerrada'}</Badge>
              {s.next_dates?.length > 0 && <span className="text-muted-foreground">Próximas: {s.next_dates.map(dLocal).join(', ')}</span>}
              <div className="ml-auto flex gap-1">
                <Button size="icon" variant="ghost" className="h-6 w-6" title="Ver detalhes" onClick={(event) => { event.stopPropagation(); setSelectedSchedule(s); }}><Eye className="h-3 w-3" /></Button>
                {s.status === 'active' && <Button size="icon" variant="ghost" className="h-6 w-6" title="Pausar" onClick={(event) => { event.stopPropagation(); actions.setScheduleStatus.mutate({ p_schedule_id: s.id, p_status: 'paused' }); }}><Pause className="h-3 w-3" /></Button>}
                {s.status === 'paused' && <Button size="icon" variant="ghost" className="h-6 w-6" title="Retomar" onClick={(event) => { event.stopPropagation(); actions.setScheduleStatus.mutate({ p_schedule_id: s.id, p_status: 'active' }); }}><Play className="h-3 w-3" /></Button>}
                {s.status !== 'ended' && <Button size="icon" variant="ghost" className="h-6 w-6" title="Encerrar" onClick={(event) => { event.stopPropagation(); actions.setScheduleStatus.mutate({ p_schedule_id: s.id, p_status: 'ended' }); }}><Square className="h-3 w-3" /></Button>}
              </div>
            </div>
          ))}
        </div>
      ))}

      {section === 'inspections' && (isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : !filteredRows.length ? (
        <p className="text-sm text-muted-foreground py-8 text-center flex flex-col items-center gap-2">
          <ClipboardCheck className="h-6 w-6 opacity-50" />
          {rows?.length ? 'Nenhuma vistoria neste filtro.' : 'Nenhuma vistoria para este veículo.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[1480px] text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr className="text-left">
                 <th className="p-2">Nº</th><th className="p-2">Modelo</th>
                 {/* ref usada para alinhar a linha de filtros acima desta coluna (v1.5.2) */}
                 <th ref={typeThRef} className="p-2">Tipo checklist</th><th className="p-2">Status</th>
                 <th className="p-2">Prazo</th><th className="p-2">Progresso</th><th className="p-2">Tipo responsável</th><th className="p-2">Responsável</th>
                 <th className="p-2">Periodicidade</th><th className="p-2">Origem</th><th className="p-2">Criada em</th><th className="p-2">Concluída em</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => setOpenId(r.id)}>
                  <td className="p-2 font-medium">#{r.code}</td>
                  <td className="p-2">{r.template_name} <span className="text-muted-foreground">v{r.version_number}</span></td>
                   <td className="p-2">{r.checklist_type_name || '—'}</td>
                  <td className="p-2">
                     <StatusDotLabel tone={INSPECTION_STATUS[r.status].tone} label={INSPECTION_STATUS[r.status].label} />
                    {r.fraud_alerts > 0 && <AlertTriangle className="inline h-3.5 w-3.5 ml-1 text-destructive" />}
                  </td>
                  <td className={cn('p-2', r.deadline_status.includes('overdue') && 'text-destructive')}>
                    {d(r.due_finish_at, 'dd/MM HH:mm')} {DEADLINE_LABEL[r.deadline_status] && <span className="block">{DEADLINE_LABEL[r.deadline_status]}</span>}
                  </td>
                   <td className="p-2">
                     <div className="flex min-w-28 items-center gap-2">
                       <Progress value={r.items_total ? (r.items_answered / r.items_total) * 100 : 0} className="h-1.5 flex-1" />
                       <span className="shrink-0 tabular-nums text-muted-foreground">{r.items_answered}/{r.items_total}</span>
                     </div>
                   </td>
                   <td className="p-2">{ASSIGNEE_MODE[r.assignee_mode] ?? '—'}</td>
                   <td className="p-2">
                     <div className="flex min-w-48 items-center gap-2">
                       <SmartAvatar src={r.assignee_avatar} name={r.assignee_name || r.queue_name || 'Responsável'} className="h-7 w-7" />
                       <div className="min-w-0">
                         <p className="truncate font-medium">{r.assignee_name || r.queue_name || '—'}</p>
                         {r.executor_name && r.executor_name !== r.assignee_name && <p className="truncate text-[10px] text-muted-foreground">Executor: {r.executor_name}</p>}
                         {r.reviewer_name && <p className="truncate text-[10px] text-muted-foreground">Revisor: {r.reviewer_name}</p>}
                       </div>
                     </div>
                   </td>
                   <td className="p-2">{r.schedule_frequency ? FREQ[r.schedule_frequency] ?? r.schedule_frequency : '—'}</td>
                   <td className="p-2">{r.origin === 'schedule' ? 'Automática' : 'Manual'}</td>
                   <td className="p-2 whitespace-nowrap">{d(r.created_at, 'dd/MM/yyyy HH:mm')}</td>
                   <td className="p-2 whitespace-nowrap">{d(r.finished_at, 'dd/MM/yyyy HH:mm')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <NewInspectionDialog open={newOpen} onOpenChange={setNewOpen} vehicleId={vehicleId}
        onCreated={(id) => { setNewOpen(false); setOpenId(id); }} />
      <ScheduleDialog open={schedOpen || !!editingSchedule} onOpenChange={(open) => { setSchedOpen(open); if (!open) setEditingSchedule(null); }} vehicleId={vehicleId} schedule={editingSchedule} />
      <ScheduleDetailsDialog schedule={selectedSchedule} onOpenChange={(open) => !open && setSelectedSchedule(null)}
        onEdit={(schedule) => { setSelectedSchedule(null); setEditingSchedule(schedule); }} />
      <InspectionDialog instanceId={openId} onOpenChange={(o) => !o && setOpenId(null)} />
    </div>
  );
}

/** Nova vistoria: escolhe o Template de Checklist (somente ativos e publicados) */
function NewInspectionDialog({ open, onOpenChange, vehicleId, onCreated }: {
  open: boolean; onOpenChange: (o: boolean) => void; vehicleId: string; onCreated: (id: string) => void;
}) {
  const { data: templates } = useInspectionTemplatesForSelection(open);
  const { create } = useInspectionActions();
  const [tpl, setTpl] = useState('');
  const [due, setDue] = useState('');
  const [typeId, setTypeId] = useState('');
  const [asg, setAsg] = useState<{ mode: AssigneeMode; userId: string | null; queueId: string }>({ mode: 'user', userId: null, queueId: '' });
  const filtered = (templates ?? []).filter((t) => t.type_id === typeId);
  const selected = filtered.find((t) => t.id === tpl);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] gap-0 overflow-hidden p-0 sm:max-w-lg flex flex-col">
        <PremiumDialogHeader icon={ClipboardCheck} title="Nova vistoria" description="Selecione o modelo, o prazo e quem será responsável." />
        {/* min-h-0 é obrigatório para a rolagem interna funcionar dentro do flex column do DialogContent */}
        <ScrollArea className="min-h-0 flex-1"><div className="space-y-4 px-6 py-5">
          <FormSectionCard title="Modelo da vistoria" icon={ClipboardCheck} compact>
          <div className="space-y-1">
            <Label>Tipo de Checklist</Label>
            <ChecklistTypeSelect value={typeId} onChange={(v) => { setTypeId(v); setTpl(''); }} />
          </div>
          <div className="space-y-1">
            <Label>Template de Checklist</Label>
            <Select value={tpl} onValueChange={setTpl} disabled={!typeId}>
              <SelectTrigger><SelectValue placeholder={typeId ? 'Selecione' : 'Escolha o tipo primeiro'} /></SelectTrigger>
              <SelectContent>
                {filtered.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} (v{t.version_number} · {t.items_count} itens)</SelectItem>)}
              </SelectContent>
            </Select>
            {typeId && templates && !filtered.length && <p className="text-xs text-muted-foreground">Nenhum template publicado deste tipo.</p>}
            {templates && !templates.length && <p className="text-xs text-muted-foreground">Nenhum modelo ativo e publicado. Publique um em Configurações → Template de Checklist.</p>}
          </div>
          {selected?.deadline_mode === 'fixed' && (
            <div className="space-y-1"><Label>Data limite</Label><Input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} /></div>
          )}
          </FormSectionCard>
          <FormSectionCard title="Responsável" icon={UserRound} compact><AssigneeFields open={open} mode={asg.mode} userId={asg.userId} queueId={asg.queueId}
            onChange={(p) => setAsg((a) => ({ ...a, ...p }))} />
          </FormSectionCard>
        </div></ScrollArea>
        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!tpl || create.isPending || (selected?.deadline_mode === 'fixed' && !due) || (asg.mode === 'queue' && !asg.queueId) || (asg.mode === 'user' && !asg.userId)}
            onClick={async () => {
              const id = await create.mutateAsync({
                p_vehicle_id: vehicleId, p_template_id: tpl,
                p_executor_id: asg.mode === 'user' ? asg.userId : null,
                p_due_finish_at: due ? new Date(due).toISOString() : null, p_notes: null,
                p_assignee_mode: asg.mode, p_queue_id: asg.mode === 'queue' ? asg.queueId : null,
              });
              setTpl(''); setDue(''); setTypeId(''); setAsg({ mode: 'user', userId: null, queueId: '' });
              onCreated(id as string);
            }}>Criar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Periodicidade: gera vistorias automaticamente (fuso de São Paulo) */
function ScheduleDialog({ open, onOpenChange, vehicleId, schedule }: { open: boolean; onOpenChange: (o: boolean) => void; vehicleId: string; schedule?: ScheduleRow | null }) {
  const { data: templates } = useInspectionTemplatesForSelection(open);
  const { upsertSchedule } = useInspectionActions();
  const [f, setF] = useState({
    template_id: '', frequency: 'weekly', day_of_week: '1', day_of_month: '1', time_of_day: '08:00',
    starts_on: format(new Date(), 'yyyy-MM-dd'), ends_mode: 'never', ends_on: '', max_occurrences: '', skip_if_open: true,
    assignee_mode: 'user' as AssigneeMode, assignee_user_id: null as string | null, assignee_queue_id: '',
  });
  const [typeId, setTypeId] = useState('');
  const filtered = (templates ?? []).filter((t) => t.type_id === typeId);
  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (schedule) {
      setTypeId(schedule.type_id ?? '');
      setF({
        template_id: schedule.template_id, frequency: schedule.frequency, day_of_week: String(schedule.day_of_week ?? 1),
        day_of_month: String(schedule.day_of_month ?? 1), time_of_day: String(schedule.time_of_day).slice(0, 5), starts_on: schedule.starts_on,
        ends_mode: schedule.ends_mode, ends_on: schedule.ends_on ?? '', max_occurrences: schedule.max_occurrences ? String(schedule.max_occurrences) : '',
        skip_if_open: schedule.skip_if_open, assignee_mode: schedule.assignee_mode ?? 'user', assignee_user_id: schedule.assignee_user_id ?? null,
        assignee_queue_id: schedule.assignee_queue_id ?? '',
      });
    }
  }, [open, schedule]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[90dvh] max-h-[90dvh] gap-0 overflow-hidden p-0 sm:max-w-xl flex flex-col">
        <PremiumDialogHeader icon={Repeat} title={schedule ? 'Editar periodicidade' : 'Vistoria periódica'} description="Configure quando as vistorias serão criadas e quem ficará responsável." />
        {/* min-h-0 é obrigatório para a rolagem interna funcionar dentro do flex column do DialogContent */}
        <ScrollArea className="min-h-0 flex-1"><div className="space-y-4 px-6 py-5">
        <FormSectionCard title="Modelo" icon={ClipboardCheck} compact><div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label>Tipo de Checklist</Label>
            <ChecklistTypeSelect value={typeId} onChange={(v) => { setTypeId(v); set('template_id', ''); }} />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Template de Checklist</Label>
            <Select value={f.template_id} onValueChange={(v) => set('template_id', v)} disabled={!typeId}>
              <SelectTrigger><SelectValue placeholder={typeId ? 'Selecione' : 'Escolha o tipo primeiro'} /></SelectTrigger>
              <SelectContent>{filtered.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Frequência</Label>
            <Select value={f.frequency} onValueChange={(v) => set('frequency', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(FREQ).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {f.frequency === 'weekly' && (
            <div className="space-y-1">
              <Label>Dia da semana</Label>
              <Select value={f.day_of_week} onValueChange={(v) => set('day_of_week', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={i} value={String(i)}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.frequency === 'monthly' && (
            <div className="space-y-1"><Label>Dia do mês (1–28)</Label><Input type="number" min={1} max={28} value={f.day_of_month} onChange={(e) => set('day_of_month', e.target.value)} /></div>
          )}
        </div></FormSectionCard>
        <FormSectionCard title="Agenda e término" icon={CalendarRange} compact><div className="grid grid-cols-2 gap-3">
           <div className="space-y-1"><Label>Horário de criação</Label><Input type="time" value={f.time_of_day} onChange={(e) => set('time_of_day', e.target.value)} /></div>
          <div className="space-y-1"><Label>Início</Label><Input type="date" value={f.starts_on} onChange={(e) => set('starts_on', e.target.value)} /></div>
          <div className="space-y-1">
            <Label>Término</Label>
            <Select value={f.ends_mode} onValueChange={(v) => set('ends_mode', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Nunca</SelectItem>
                <SelectItem value="on_date">Em uma data</SelectItem>
                <SelectItem value="after_n">Após N vistorias</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.ends_mode === 'on_date' && <div className="space-y-1"><Label>Data final</Label><Input type="date" value={f.ends_on} onChange={(e) => set('ends_on', e.target.value)} /></div>}
          {f.ends_mode === 'after_n' && <div className="space-y-1"><Label>Quantidade</Label><Input type="number" min={1} value={f.max_occurrences} onChange={(e) => set('max_occurrences', e.target.value)} /></div>}
          <div className="col-span-2 flex items-center justify-between">
            <Label>Não gerar se houver vistoria em aberto</Label>
            <Switch checked={f.skip_if_open} onCheckedChange={(v) => set('skip_if_open', v)} />
          </div>
        </div></FormSectionCard>
        <FormSectionCard title="Responsável" icon={UserRound} compact>
          <div>
            <AssigneeFields open={open} mode={f.assignee_mode} userId={f.assignee_user_id} queueId={f.assignee_queue_id}
              onChange={(p) => setF((prev) => ({
                ...prev,
                ...(p.mode !== undefined && { assignee_mode: p.mode }),
                ...(p.userId !== undefined && { assignee_user_id: p.userId }),
                ...(p.queueId !== undefined && { assignee_queue_id: p.queueId }),
              }))} />
          </div>
        </FormSectionCard>
        </div></ScrollArea>
        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!f.template_id || upsertSchedule.isPending || (f.assignee_mode === 'queue' && !f.assignee_queue_id) || (f.assignee_mode === 'user' && !f.assignee_user_id)}
            onClick={async () => {
              await upsertSchedule.mutateAsync({ p_data: {
                ...f, vehicle_id: vehicleId,
                assignee_user_id: f.assignee_mode === 'user' ? f.assignee_user_id : null,
                assignee_queue_id: f.assignee_mode === 'queue' ? f.assignee_queue_id : null,
              }, p_schedule_id: schedule?.id ?? null });
              onOpenChange(false);
            }}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Leitura completa da periodicidade usando o objeto já carregado na aba. */
function ScheduleDetailsDialog({ schedule, onOpenChange, onEdit }: {
  schedule: ScheduleRow | null; onOpenChange: (open: boolean) => void; onEdit: (schedule: ScheduleRow) => void;
}) {
  if (!schedule) return null;
  const modeLabel = schedule.assignee_mode === 'queue' ? 'Fila global' : schedule.assignee_mode === 'owner' ? 'Cliente/proprietário' : 'Usuário';
  const ending = schedule.ends_mode === 'on_date' ? `Em ${d(schedule.ends_on)}` : schedule.ends_mode === 'after_n' ? `Após ${schedule.max_occurrences ?? 0} vistorias` : 'Nunca';
  const frequency = `${FREQ[schedule.frequency] ?? schedule.frequency}${schedule.frequency === 'weekly' ? ` · ${WEEKDAYS[schedule.day_of_week]}` : ''}${schedule.frequency === 'monthly' ? ` · dia ${schedule.day_of_month}` : ''}`;
  const responsible = schedule.assignee_mode === 'queue' ? schedule.queue_name : schedule.assignee_mode === 'owner' ? 'Proprietário congelado na criação' : schedule.assignee_name;
  const Detail = ({ label, value }: { label: string; value: ReactNode }) => <div><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="mt-0.5 text-sm">{value || '—'}</div></div>;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent enableSwipeToClose={false} className="flex h-[90dvh] max-h-[90dvh] min-h-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <PremiumDialogHeader className="shrink-0" icon={Settings2} title="Detalhes da periodicidade" description="Configuração completa usada para gerar as vistorias deste veículo." />
        {/* A altura definida reserva espaço entre as faixas fixas; desativar o gesto de fechar evita disputa com o toque na rolagem. */}
        <div role="region" aria-label="Configurações da periodicidade" tabIndex={0} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5"><div className="space-y-4">
          <FormSectionCard title="Modelo" icon={ClipboardCheck} compact><div className="grid grid-cols-2 gap-3"><Detail label="Tipo" value={schedule.type_name} /><Detail label="Template" value={schedule.template_name} /><Detail label="Status" value={schedule.status === 'active' ? 'Ativa' : schedule.status === 'paused' ? 'Pausada' : 'Encerrada'} /></div></FormSectionCard>
          <FormSectionCard title="Agenda" icon={CalendarRange} compact><div className="grid grid-cols-2 gap-3"><Detail label="Frequência" value={frequency} /><Detail label="Horário local" value={String(schedule.time_of_day).slice(0, 5)} /><Detail label="Início" value={d(schedule.starts_on)} /><Detail label="Término" value={ending} /><Detail label="Ocorrências" value={schedule.occurrences_count ?? 0} /><Detail label="Com vistoria aberta" value={schedule.skip_if_open ? 'Não gerar nova' : 'Gerar normalmente'} /></div></FormSectionCard>
          <FormSectionCard title="Responsável" icon={UserRound} compact><div className="grid grid-cols-2 gap-3"><Detail label="Tipo" value={modeLabel} /><Detail label="Responsável" value={responsible} /></div></FormSectionCard>
          <FormSectionCard title="Execução" icon={Repeat} compact><div className="grid grid-cols-2 gap-3"><Detail label="Última execução" value={d(schedule.last_run_at, 'dd/MM/yyyy HH:mm')} /><Detail label="Atualizada em" value={d(schedule.updated_at, 'dd/MM/yyyy HH:mm')} /><Detail label="Próximas datas" value={schedule.next_dates?.length ? schedule.next_dates.map(dLocal).join(', ') : 'Nenhuma prevista'} /></div></FormSectionCard>
        </div></div>
        <DialogFooter className="shrink-0 border-t bg-muted/30 px-6 py-3"><Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>{schedule.status !== 'ended' && <Button onClick={() => onEdit(schedule)}>Editar</Button>}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
